import React from "react";
import classNames from "classnames";
import type { TextInputProps } from "./types";
import styles from "./TextInput.module.css";

/**
 * TextInput atom - A styled text/number input field.
 *
 * Supports text, number, email, password, tel, and url input types.
 * Designed to work with FormField molecule for labels and validation.
 *
 * @example
 * ```tsx
 * // Basic text input
 * <TextInput
 *   value={name}
 *   onChange={setName}
 *   placeholder="Enter name..."
 * />
 *
 * // Number input
 * <TextInput
 *   type="number"
 *   value={hours}
 *   onChange={(val) => setHours(parseFloat(val) || 0)}
 *   min={0}
 *   max={24}
 *   step={0.5}
 * />
 * ```
 */
export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      value,
      onChange,
      type = "text",
      placeholder,
      size = "md",
      disabled = false,
      readOnly = false,
      error = false,
      fullWidth = false,
      min,
      max,
      step,
      className,
      "data-testid": testId,
      ...props
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    };

    return (
      <input
        ref={ref}
        type={type}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        min={min}
        max={max}
        step={step}
        className={classNames(
          styles.input,
          styles[size],
          {
            [styles.error]: error,
            [styles.disabled]: disabled,
            [styles.fullWidth]: fullWidth,
          },
          className,
        )}
        data-testid={testId}
        {...props}
      />
    );
  },
);

TextInput.displayName = "TextInput";

export default TextInput;
